import { connectorHandlerCode } from "../../../../constants/ConnectorHandler";

// How each network lays a post out, so the preview reads like the real feed rather than
// one generic card. Only the parts that differ between networks are described here; the
// components in PreviewItem.tsx draw them.

export interface PreviewAction {
    icon: string;
    /** peerActivity:<label>; icon-only when absent. */
    label?: string;
}

export interface NetworkSkin {
    /** Feed card (most networks), Telegram's message bubble, YouTube's video card, a pin. */
    layout: 'card' | 'bubble' | 'video' | 'pin';
    /** Text above the media (VK, Facebook, X...) or a caption under it (Instagram). */
    textOnTop: boolean;
    /** Several images: a grid, or Instagram's swipeable carousel. */
    media: 'grid' | 'carousel';
    /** Instagram starts the caption with the account name in bold. */
    captionWithName?: boolean;
    /** X shows the avatar beside the whole post and the @handle after the name. */
    sideAvatar?: boolean;
    handle?: boolean;
    /** Facebook and LinkedIn show a globe after the time. */
    publicMark?: boolean;
    actions: PreviewAction[];
    /** Labelled actions spread over the width (Facebook, LinkedIn) or icons in a row. */
    spreadActions?: boolean;
    /** Actions pushed to the right end, e.g. Instagram's bookmark. */
    endActions?: PreviewAction[];
    /** A views counter at the end of the action bar (VK). */
    views?: boolean;
}

const DEFAULT: NetworkSkin = {
    layout: 'card',
    textOnTop: true,
    media: 'grid',
    actions: [{ icon: 'ri-heart-3-line' }, { icon: 'ri-chat-3-line' }, { icon: 'ri-share-forward-line' }],
};

const SKINS: Record<string, Partial<NetworkSkin>> = {
    [connectorHandlerCode.CONNECTOR_HANDLER_VK]: { views: true },
    [connectorHandlerCode.CONNECTOR_HANDLER_OK]: {
        actions: [{ icon: 'ri-chat-3-line' }, { icon: 'ri-share-forward-line' }, { icon: 'ri-thumb-up-line' }],
    },
    [connectorHandlerCode.CONNECTOR_HANDLER_FACEBOOK]: {
        publicMark: true,
        spreadActions: true,
        actions: [
            { icon: 'ri-thumb-up-line', label: 'previewLike' },
            { icon: 'ri-chat-3-line', label: 'previewComment' },
            { icon: 'ri-share-forward-line', label: 'previewShare' },
        ],
    },
    [connectorHandlerCode.CONNECTOR_HANDLER_LINKEDIN]: {
        publicMark: true,
        spreadActions: true,
        actions: [
            { icon: 'ri-thumb-up-line', label: 'previewLike' },
            { icon: 'ri-chat-3-line', label: 'previewComment' },
            { icon: 'ri-repeat-line', label: 'previewRepost' },
            { icon: 'ri-send-plane-line', label: 'previewSend' },
        ],
    },
    [connectorHandlerCode.CONNECTOR_HANDLER_TWITTER]: {
        sideAvatar: true,
        handle: true,
        actions: [{ icon: 'ri-chat-1-line' }, { icon: 'ri-repeat-line' }, { icon: 'ri-heart-3-line' }, { icon: 'ri-bar-chart-line' }],
        endActions: [{ icon: 'ri-bookmark-line' }, { icon: 'ri-upload-2-line' }],
    },
    [connectorHandlerCode.CONNECTOR_HANDLER_INSTAGRAM]: {
        textOnTop: false,
        media: 'carousel',
        captionWithName: true,
        actions: [{ icon: 'ri-heart-3-line' }, { icon: 'ri-chat-3-line' }, { icon: 'ri-send-plane-line' }],
        endActions: [{ icon: 'ri-bookmark-line' }],
    },
    [connectorHandlerCode.CONNECTOR_HANDLER_TELEGRAM]: { layout: 'bubble', textOnTop: false, actions: [] },
    [connectorHandlerCode.CONNECTOR_HANDLER_TELEGRAM_BOT]: { layout: 'bubble', textOnTop: false, actions: [] },
    [connectorHandlerCode.CONNECTOR_HANDLER_YOUTUBE]: { layout: 'video', textOnTop: false, actions: [] },
    [connectorHandlerCode.CONNECTOR_HANDLER_PINTEREST]: {
        layout: 'pin',
        textOnTop: false,
        media: 'carousel',
        actions: [],
    },
    [connectorHandlerCode.CONNECTOR_HANDLER_TIKTOK]: { textOnTop: false, media: 'carousel' },
};

export const networkSkin = (handlerCode?: string): NetworkSkin => ({ ...DEFAULT, ...(handlerCode ? SKINS[handlerCode] : {}) });

/** The account handle as the network prints it ("@name"), taken from the channel link. */
export const channelHandle = (channelLink?: string, fallback?: string): string => {
    const path = (channelLink || '').replace(/^https?:\/\/[^/]+/i, '').split(/[/?#]/).filter(Boolean)[0];
    const name = (path || fallback || '').replace(/^@/, '').replace(/\s+/g, '').toLowerCase();

    return name ? '@' + name : '';
};

/** How a grid places n images: Facebook/VK/X-style 1, 2, 3 (one big + two), 4+ (2x2, "+k" on the last). */
export const gridLayout = (count: number): { shown: number; className: string; more: number } => {
    if (count <= 1) {
        return { shown: count, className: 'previewGrid1', more: 0 };
    }
    if (count === 2) {
        return { shown: 2, className: 'previewGrid2', more: 0 };
    }
    if (count === 3) {
        return { shown: 3, className: 'previewGrid3', more: 0 };
    }

    return { shown: 4, className: 'previewGrid4', more: count - 4 };
};
